import { useState, useRef, useEffect, useCallback, type FormEvent } from "react";
import { Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { ChatMessage } from "@/components/chat/ChatMessage";
import type { ChatMessage as ChatMessageType } from "@/hooks/useChat";
import { transcriptDisplayTitle, type TranscriptDetail } from "@/hooks/useTranscripts";
import { formatError } from "@/lib/utils";

// Per-transcript AI chat. Same pattern as ItemChat: context prefix on the
// first message, global `chat` edge fn does the thinking (it has the
// read_transcript tool, so it can pull the full transcript by id whenever
// the question needs verbatim detail), history persists to
// transcript_chat_messages.
export function TranscriptChat({ transcript }: { transcript: TranscriptDetail }) {
  const [input, setInput] = useState("");
  const [pendingMessages, setPendingMessages] = useState<ChatMessageType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const bottomRef = useRef<HTMLDivElement>(null);

  const { data: savedMessages } = useQuery({
    queryKey: ["transcript-chat", transcript.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("transcript_chat_messages")
        .select("role, content")
        .eq("transcript_id", transcript.id)
        .order("created_at");
      if (error) throw error;
      return (data || []) as ChatMessageType[];
    },
    enabled: !!transcript.id,
  });

  const messages: ChatMessageType[] = [...(savedMessages || []), ...pendingMessages];

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const send = useCallback(
    async (text: string) => {
      if (!user) return;
      const userMsg: ChatMessageType = { role: "user", content: text };
      setPendingMessages((prev) => [...prev, userMsg]);
      setIsLoading(true);

      try {
        const title = transcriptDisplayTitle(transcript);
        const when = transcript.meeting_date
          ? new Date(transcript.meeting_date).toLocaleString()
          : "unknown date";
        const contextPrefix = `[Context: I'm looking at the meeting transcript "${title}" — source: ${transcript.source}, date: ${when}, transcript id: ${transcript.id}. Speakers: ${(transcript.speakers || []).join(", ") || "unknown"}.${transcript.summary ? ` Summary: ${transcript.summary}` : ""}\nWhen you need verbatim detail or anything not covered by the summary, call the read_transcript tool with id "${transcript.id}" to read the full transcript before answering.]\n\n`;

        const allMessages = [...(savedMessages || []), ...pendingMessages, userMsg];
        const messagesForApi = allMessages.map((m, i) => ({
          role: m.role,
          content: i === 0 && m.role === "user" ? contextPrefix + m.content : m.content,
        }));

        const { data, error } = await supabase.functions.invoke("chat", {
          body: { messages: messagesForApi, user_id: user.id },
        });
        if (error) throw error;

        const assistantMsg: ChatMessageType = {
          role: "assistant",
          content: data.response || "Hmm, couldn't process that.",
        };

        await supabase.from("transcript_chat_messages").insert([
          { transcript_id: transcript.id, user_id: user.id, role: "user", content: text },
          {
            transcript_id: transcript.id,
            user_id: user.id,
            role: "assistant",
            content: assistantMsg.content,
          },
        ]);

        setPendingMessages([]);
        queryClient.invalidateQueries({ queryKey: ["transcript-chat", transcript.id] });
      } catch (err) {
        setPendingMessages((prev) => [
          ...prev,
          { role: "assistant", content: `Error: ${formatError(err)}` },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [savedMessages, pendingMessages, user, transcript, queryClient],
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || isLoading) return;
    setInput("");
    send(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3">
        {messages.length === 0 && (
          <div className="flex items-center justify-center h-full text-sm text-muted-foreground text-center px-6">
            Ask anything about this meeting — who said what, decisions, follow-ups. I can read the full transcript.
          </div>
        )}
        {messages.map((msg, i) => (
          <ChatMessage key={i} message={msg} />
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-muted text-muted-foreground rounded-lg px-3 py-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="shrink-0 border-t border-border px-4 py-3">
        <form onSubmit={handleSubmit} className="flex gap-2 items-end">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about this meeting..."
            rows={1}
            className="flex-1 resize-none bg-muted rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary min-h-[40px] max-h-[100px]"
            style={{ fieldSizing: "content" } as React.CSSProperties}
          />
          <Button
            type="submit"
            size="icon"
            className="h-10 w-10 rounded-full shrink-0"
            disabled={isLoading || !input.trim()}
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </form>
      </div>
    </div>
  );
}
